import {
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

import { db } from "@/lib/firebase";

type NamedItem = {
  name?: string | null;
  title?: string | null;
  description?: string | null;
  shortDescription?: string | null;
  details?: string | null;
  summary?: string | null;
};

export type BusinessStatus =
  | "draft"
  | "pending"
  | "approved"
  | "rejected"
  | "changes_requested";

export type BuildBusinessSearchInput = {
  businessId: string;
  slug?: string | null;

  businessName: string;

  categoryId?: string | null;
  categoryName?: string | null;

  subcategoryId?: string | null;
  subcategoryName?: string | null;

  countyId?: string | null;
  countyName?: string | null;

  cityId?: string | null;
  cityName?: string | null;

  areaId?: string | null;
  areaName?: string | null;

  shortDescription?: string | null;

  logoUrl?: string | null;
  coverUrl?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  website?: string | null;

  isVerified?: boolean;
  isFeatured?: boolean;
  featuredRank?: number | null;
  hasProfileImage?: boolean;
  hasLocationPin?: boolean;

  status?: string | null;
  isDeleted?: boolean;
  isPublished?: boolean;
  isDeactivated?: boolean;

  subscriptionStatus?: string | null;
  subscriptionType?: string | null;
  subscriptionPlan?: string | null;

  items?: NamedItem[];
};

export type BusinessSearchDoc = {
  businessId: string;
  slug: string | null;

  businessName: string;
  businessNameLower: string;

  categoryId: string | null;
  category: string | null;
  categoryName: string | null;
  categoryNameLower: string;

  subcategoryId: string | null;
  subcategory: string | null;
  subcategoryName: string | null;
  subcategoryNameLower: string;

  countyId: string | null;
  county: string | null;
  countyName: string | null;

  cityId: string | null;
  city: string | null;
  cityName: string | null;

  areaId: string | null;
  area: string | null;
  areaName: string | null;

  shortDescription: string | null;

  logoUrl: string | null;
  coverUrl: string | null;
  phone: string | null;
  whatsapp: string | null;
  website: string | null;

  isVerified: boolean;
  isFeatured: boolean;
  featuredRank: number | null;

  status: BusinessStatus;
  isDeleted: boolean;
  isPublished: boolean;
  isDeactivated: boolean;

  /**
   * Optional.
   *
   * Important:
   * Do not write this field as null.
   * Your Firestore rules treat missing subscriptionStatus as allowed,
   * but a null subscriptionStatus would make the search document private.
   */
  subscriptionStatus?: string;
  subscriptionType?: string;
  subscriptionPlan?: string;

  searchWords: string[];

  /**
   * Compatibility field for your existing indexes/search code.
   * Keep this equal to searchWords.
   */
  searchTokens: string[];

  searchPrefixes: string[];
  searchText: string;
  tokens: string[];
  phraseTokens: string[];
  itemTokens: string[];
  locationTokens: string[];
  categoryTokens: string[];
  searchableText: string;

  itemCount: number;
  hasLogo: boolean;
  hasProfileImage: boolean;
  hasLocationPin: boolean;
  hasWhatsApp: boolean;

  updatedAt: unknown;
  indexedAt: unknown;
};

const SEARCH_TOKEN_LIMIT = 350;
const SEARCH_PREFIX_LIMIT = 450;
const SCOPED_TOKEN_LIMIT = 150;
const PHRASE_TOKEN_LIMIT = 150;

const STANDALONE_STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "at",
  "by",
  "for",
  "from",
  "in",
  "near",
  "of",
  "on",
  "or",
  "our",
  "the",
  "to",
  "we",
  "with",
]);

export function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function cleanObject<T extends Record<string, unknown>>(value: T): T {
  const next: Record<string, unknown> = {};

  for (const [key, val] of Object.entries(value)) {
    if (val === undefined) continue;
    next[key] = val;
  }

  return next as T;
}

function cleanString(value: unknown): string | null {
  if (typeof value !== "string") return null;

  const clean = value.trim();
  return clean.length > 0 ? clean : null;
}

function cleanBoolean(value: unknown): boolean {
  if (value === true) return true;
  if (value === false) return false;

  if (typeof value === "string") {
    const clean = value.trim().toLowerCase();

    if (clean === "true") return true;
    if (clean === "false") return false;
    if (clean === "yes") return true;
    if (clean === "no") return false;
    if (clean === "1") return true;
    if (clean === "0") return false;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  return false;
}

function cleanNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const clean = value.trim();
    if (!clean) return null;

    const parsed = Number(clean);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function uniqueStrings(
  values: (string | null | undefined)[],
  maxCount = Number.POSITIVE_INFINITY
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  for (const value of values) {
    const clean = (value ?? "").trim();
    if (!clean || seen.has(clean)) continue;

    seen.add(clean);
    out.push(clean);

    if (out.length >= maxCount) break;
  }

  return out;
}

function tokenizePhrase(value: string): string[] {
  const normalized = normalizeText(value);
  if (!normalized) return [];

  return normalized.split(" ").filter(Boolean);
}

function makeWordPrefixes(word: string, maxLen = 12): string[] {
  const normalized = normalizeText(word);

  if (!normalized || normalized.includes(" ")) return [];

  const out: string[] = [];
  const limit = Math.min(normalized.length, maxLen);

  for (let i = 1; i <= limit; i += 1) {
    out.push(normalized.slice(0, i));
  }

  return out;
}

function isSearchableWord(token: string): boolean {
  return token.length > 1 && !STANDALONE_STOP_WORDS.has(token);
}

function getSearchableWords(tokens: string[]): string[] {
  return tokens.filter(isSearchableWord);
}

function isMeaningfulPhrase(tokens: string[]): boolean {
  if (tokens.length < 2 || tokens.length > 4) return false;
  if (!isSearchableWord(tokens[0])) return false;
  if (!isSearchableWord(tokens[tokens.length - 1])) return false;

  const searchableCount = getSearchableWords(tokens).length;
  if (tokens.length === 4) return searchableCount >= 3;

  return searchableCount >= 2;
}

function buildMeaningfulPhrases(tokens: string[]): string[] {
  const out = new Set<string>();

  for (let start = 0; start < tokens.length; start += 1) {
    const maxEnd = Math.min(tokens.length, start + 4);

    for (let end = start + 2; end <= maxEnd; end += 1) {
      const slice = tokens.slice(start, end);
      if (!isMeaningfulPhrase(slice)) continue;

      const phrase = slice.join(" ").trim();
      if (phrase) out.add(phrase);
    }
  }

  return Array.from(out);
}

function extractItemSearchPhrases(items?: NamedItem[]): string[] {
  if (!items?.length) return [];

  return uniqueStrings(
    items.flatMap((item) => [
      item.name,
      item.title,
      item.description,
      item.shortDescription,
      item.details,
      item.summary,
    ])
  );
}

function hasItemSearchText(item: NamedItem): boolean {
  return extractItemSearchPhrases([item]).length > 0;
}

function addSimpleAliases(words: string[]): string[] {
  const out = new Set<string>(words);

  for (const word of words) {
    if (!word || word.includes(" ")) continue;
    if (!isSearchableWord(word)) continue;
    if (word.endsWith("ing") || word.endsWith("y")) continue;

    if (word.endsWith("ies") && word.length > 4) {
      out.add(`${word.slice(0, -3)}y`);
      continue;
    }

    if (word.endsWith("s") && !word.endsWith("ss") && word.length > 4) {
      out.add(word.slice(0, -1));
    } else if (!word.endsWith("s") && word.length >= 4) {
      out.add(`${word}s`);
    }
  }

  return Array.from(out);
}

function buildPhraseAliases(phrase: string): string[] {
  const tokens = tokenizePhrase(phrase);
  if (!isMeaningfulPhrase(tokens)) return [];

  const out = new Set<string>();
  out.add(tokens.join(" "));

  tokens.forEach((token, index) => {
    if (!isSearchableWord(token)) return;

    for (const alias of addSimpleAliases([token])) {
      if (alias === token) continue;

      const next = [...tokens];
      next[index] = alias;
      if (isMeaningfulPhrase(next)) out.add(next.join(" "));
    }
  });

  return Array.from(out);
}

function buildTokenSet(phrases: (string | null | undefined)[]) {
  const normalizedPhrases = uniqueStrings(
    phrases.map((phrase) => normalizeText(phrase ?? ""))
  );

  const wordTokens = uniqueStrings(
    normalizedPhrases.flatMap((phrase) =>
      getSearchableWords(tokenizePhrase(phrase))
    )
  );

  const meaningfulPhrases = uniqueStrings(
    normalizedPhrases.flatMap((phrase) =>
      buildMeaningfulPhrases(tokenizePhrase(phrase))
    )
  );

  const phraseTokens = uniqueStrings(
    [
      ...meaningfulPhrases,
      ...meaningfulPhrases.flatMap((phrase) => buildPhraseAliases(phrase)),
    ],
    PHRASE_TOKEN_LIMIT
  );

  const aliasWordTokens = uniqueStrings(addSimpleAliases(wordTokens));

  const tokens = uniqueStrings(
    addSimpleAliases([
      ...wordTokens,
      ...aliasWordTokens,
      ...phraseTokens,
    ]),
    SEARCH_TOKEN_LIMIT
  );

  const searchPrefixes = uniqueStrings(
    aliasWordTokens.flatMap((word) => makeWordPrefixes(word, 12)),
    SEARCH_PREFIX_LIMIT
  );

  const searchableText = uniqueStrings([
    ...wordTokens,
    ...phraseTokens,
  ]).join(" ");

  return {
    tokens,
    phraseTokens,
    searchPrefixes,
    searchableText,
  };
}

function normalizeBusinessStatus(status?: string | null): BusinessStatus {
  switch (status) {
    case "draft":
    case "pending":
    case "approved":
    case "rejected":
    case "changes_requested":
      return status;

    default:
      return "draft";
  }
}

function readNamedItems(value: unknown): NamedItem[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((item): NamedItem | null => {
      if (!item || typeof item !== "object") return null;

      const row = item as Record<string, unknown>;

      return {
        name: cleanString(row.name),
        title: cleanString(row.title),
        description: cleanString(row.description),
        shortDescription: cleanString(row.shortDescription),
        details: cleanString(row.details),
        summary: cleanString(row.summary),
      };
    })
    .filter((item): item is NamedItem => item !== null);
}

function normalizeSubscriptionStatus(value: unknown): string | undefined {
  const status = cleanString(value);
  if (!status) return undefined;

  return status.toLowerCase();
}

function normalizePlanLabel(value: unknown): string | undefined {
  const label = cleanString(value);
  return label || undefined;
}

export function buildBusinessSearchPayload(
  input: BuildBusinessSearchInput
): BusinessSearchDoc {
  const itemPhrases = extractItemSearchPhrases(input.items);
  const categoryPhrases = uniqueStrings([
    input.categoryName,
    input.subcategoryName,
  ]);
  const locationPhrases = uniqueStrings([
    input.countyName,
    input.cityName,
    input.areaName,
  ]);

  const phrases = uniqueStrings([
    input.businessName,
    input.slug,
    ...categoryPhrases,
    ...locationPhrases,
    input.shortDescription,
    ...itemPhrases,
  ]);

  const searchTerms = buildTokenSet(phrases);
  const itemTerms = buildTokenSet(itemPhrases);
  const categoryTerms = buildTokenSet(categoryPhrases);
  const locationTerms = buildTokenSet(locationPhrases);

  const subscriptionStatus = normalizeSubscriptionStatus(
    input.subscriptionStatus
  );

  const subscriptionType = normalizePlanLabel(input.subscriptionType);
  const subscriptionPlan = normalizePlanLabel(input.subscriptionPlan);

  return cleanObject({
    businessId: input.businessId,
    slug: input.slug ?? null,

    businessName: input.businessName,
    businessNameLower: normalizeText(input.businessName),

    categoryId: input.categoryId ?? null,
    category: input.categoryName ?? null,
    categoryName: input.categoryName ?? null,
    categoryNameLower: normalizeText(input.categoryName ?? ""),

    subcategoryId: input.subcategoryId ?? null,
    subcategory: input.subcategoryName ?? null,
    subcategoryName: input.subcategoryName ?? null,
    subcategoryNameLower: normalizeText(input.subcategoryName ?? ""),

    countyId: input.countyId ?? null,
    county: input.countyName ?? null,
    countyName: input.countyName ?? null,

    cityId: input.cityId ?? null,
    city: input.cityName ?? null,
    cityName: input.cityName ?? null,

    areaId: input.areaId ?? null,
    area: input.areaName ?? null,
    areaName: input.areaName ?? null,

    shortDescription: input.shortDescription ?? null,

    logoUrl: input.logoUrl ?? null,
    coverUrl: input.coverUrl ?? null,
    phone: input.phone ?? null,
    whatsapp: input.whatsapp ?? null,
    website: input.website ?? null,

    isVerified: input.isVerified === true,
    isFeatured: input.isFeatured === true,
    featuredRank: input.featuredRank ?? null,

    status: normalizeBusinessStatus(input.status),
    isDeleted: input.isDeleted === true,
    isPublished: input.isPublished === true,
    isDeactivated: input.isDeactivated === true,

    subscriptionStatus,
    subscriptionType,
    subscriptionPlan,

    searchWords: searchTerms.tokens,
    searchTokens: searchTerms.tokens,
    searchPrefixes: searchTerms.searchPrefixes,
    searchText: searchTerms.searchableText,
    tokens: searchTerms.tokens,
    phraseTokens: searchTerms.phraseTokens,
    itemTokens: itemTerms.tokens.slice(0, SCOPED_TOKEN_LIMIT),
    locationTokens: locationTerms.tokens.slice(0, SCOPED_TOKEN_LIMIT),
    categoryTokens: categoryTerms.tokens.slice(0, SCOPED_TOKEN_LIMIT),
    searchableText: searchTerms.searchableText,

    itemCount: (input.items ?? []).filter(hasItemSearchText).length,
    hasLogo: !!input.logoUrl,
    hasProfileImage: input.hasProfileImage === true,
    hasLocationPin: input.hasLocationPin === true,
    hasWhatsApp: !!input.whatsapp,

    updatedAt: serverTimestamp(),
    indexedAt: serverTimestamp(),
  }) as BusinessSearchDoc;
}

export async function upsertBusinessSearch(
  input: BuildBusinessSearchInput
): Promise<void> {
  const ref = doc(db, "businessSearch", input.businessId);
  const payload = buildBusinessSearchPayload(input);

  await setDoc(ref, payload, { merge: true });
}

export async function syncBusinessSearchFromBusiness(
  businessId: string
): Promise<void> {
  const businessRef = doc(db, "businesses", businessId);
  const businessSnap = await getDoc(businessRef);

  if (!businessSnap.exists()) {
    await deleteDoc(doc(db, "businessSearch", businessId));
    return;
  }

  const business = businessSnap.data() as Record<string, unknown>;

  const location =
    business.location && typeof business.location === "object"
      ? (business.location as Record<string, unknown>)
      : undefined;

  const businessName =
    cleanString(business.name) ??
    cleanString(business.businessName) ??
    cleanString(business.shopName) ??
    "";

  const countyId =
    cleanString(business.countyId) ??
    cleanString(location?.countyId) ??
    null;

  const countyName =
    cleanString(business.countyName) ??
    cleanString(location?.countyName) ??
    null;

  const cityId =
    cleanString(business.cityId) ??
    cleanString(location?.cityId) ??
    cleanString(location?.citySuggestionId) ??
    null;

  const cityName =
    cleanString(business.cityName) ??
    cleanString(location?.cityName) ??
    cleanString(location?.citySuggestionName) ??
    null;

  const areaId =
    cleanString(business.areaId) ??
    cleanString(location?.areaId) ??
    cleanString(location?.areaSuggestionId) ??
    null;

  const areaName =
    cleanString(business.areaName) ??
    cleanString(location?.areaName) ??
    cleanString(location?.areaSuggestionName) ??
    null;

  const categoryId =
    cleanString(business.categoryId) ??
    cleanString(business.category) ??
    null;

  const categoryName =
    cleanString(business.categoryName) ??
    cleanString(business.category) ??
    null;

  const subcategoryId =
    cleanString(business.subcategoryId) ??
    cleanString(business.subcategory) ??
    null;

  const subcategoryName =
    cleanString(business.subcategoryName) ??
    cleanString(business.subCategoryText) ??
    (Array.isArray(business.subCategory)
      ? business.subCategory
          .map((item) => cleanString(item))
          .filter((item): item is string => item !== null)
          .join(", ")
      : null) ??
    null;

  const businessType = cleanString(business.businessType) ?? "";
  const ownerShortDescription = cleanString(business.shortDescription);
  const aboutText =
    cleanString(business.about) ??
    cleanString(business.aboutText) ??
    cleanString(business.aboutUs);
  const description =
    ownerShortDescription ?? aboutText ?? cleanString(business.description) ?? "";
  const addressLine = cleanString(business.addressLine) ?? "";

  const generatedShortDescription = [
    description,
    addressLine,
    businessType === "physical_shop" ? "physical shop" : "",
    businessType === "freelancer" ? "freelancer" : "",
    businessType === "online_only" ? "online only" : "",
  ]
    .filter(Boolean)
    .join(". ")
    .trim();

  const shortDescription =
    ownerShortDescription ?? (generatedShortDescription || null);

  const items = [
    ...readNamedItems(business.items),
    ...readNamedItems(business.products),
    ...readNamedItems(business.services),
  ];
  const logoUrl = cleanString(business.logoUrl) ?? cleanString(business.logo) ?? null;
  const logoKind = cleanString(business.logoKind);
  const profileImageUrl =
    cleanString(business.profileImageUrl) ??
    cleanString(business.profilePhotoUrl) ??
    cleanString(business.photoUrl);
  const locationPin =
    business.locationPin && typeof business.locationPin === "object"
      ? (business.locationPin as Record<string, unknown>)
      : null;
  const hasLocationPin =
    typeof locationPin?.lat === "number" && typeof locationPin?.lng === "number";
  const whatsapp =
    cleanString(business.whatsappCatalogUrl) ??
    cleanString(business.whatsapp) ??
    cleanString(business.whatsappChatNumber) ??
    cleanString(business.ownerPhone) ??
    null;

  await upsertBusinessSearch({
    businessId,

    slug: cleanString(business.slug),

    businessName,

    categoryId,
    categoryName,

    subcategoryId,
    subcategoryName,

    countyId,
    countyName,

    cityId,
    cityName,

    areaId,
    areaName,

    shortDescription,

    logoUrl,

    coverUrl:
      cleanString(business.coverUrl) ?? cleanString(business.cover) ?? null,

    phone:
      cleanString(business.phone) ?? cleanString(business.ownerPhone) ?? null,

    whatsapp,

    website: cleanString(business.website) ?? cleanString(business.websiteUrl),

    isVerified: cleanBoolean(business.isVerified),
    isFeatured: cleanBoolean(business.isFeatured),

    featuredRank: cleanNumber(business.featuredRank),
    hasProfileImage:
      !!profileImageUrl || logoKind === "photo" || logoKind === "profile",
    hasLocationPin,

    status: normalizeBusinessStatus(cleanString(business.status)),
    isDeleted: cleanBoolean(business.isDeleted),
    isPublished: cleanBoolean(business.isPublished),
    isDeactivated: cleanBoolean(business.isDeactivated),

    subscriptionStatus:
      cleanString(business.subscriptionStatus) ??
      cleanString(business.listingStatus) ??
      null,

    subscriptionType:
      cleanString(business.subscriptionType) ??
      cleanString(business.planName) ??
      null,

    subscriptionPlan:
      cleanString(business.subscriptionPlan) ??
      cleanString(business.plan) ??
      null,

    items,
  });
}
